import React, { useState, useMemo } from "react";
import Navbar from "@/components/Navbar";
import { LayoutDashboard, ListChecks, Store, Truck, Percent, SlidersHorizontal, MessageSquare, CheckSquare, GitBranch, ArrowRight, Send, Sparkles } from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, LineChart, Line, RadarChart, Radar, PolarGrid, PolarAngleAxis, Cell } from "recharts";
import { EXECUTIVE_KPIS, REGION_PERFORMANCE, CATEGORY_PERFORMANCE, OPPORTUNITY_DRIVERS, SALES_TREND, OPPORTUNITIES, OUTLETS, DISTRIBUTORS, PROMOTIONS, ACTIONS, COPILOT_QA, SYNTHETIC_DISCLAIMER } from "@/data/synthetic";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Slider } from "@/components/ui/slider";
import { toast } from "sonner";
import DistributorNetworkMap from "@/components/DistributorNetworkMap";

const MENU = [
  { key: "cockpit", label: "Executive Cockpit", icon: LayoutDashboard },
  { key: "queue", label: "Opportunity Queue", icon: ListChecks },
  { key: "outlets", label: "Outlet Intelligence", icon: Store },
  { key: "distributors", label: "Distributor Health", icon: Truck },
  { key: "promos", label: "Promotion Intel", icon: Percent },
  { key: "scenario", label: "Scenario Simulator", icon: SlidersHorizontal },
  { key: "copilot", label: "AI Copilot", icon: MessageSquare },
  { key: "actions", label: "Action Tracker", icon: CheckSquare },
  { key: "lineage", label: "Data Lineage", icon: GitBranch },
];

const AXIS_STYLE = { fill: "#94A3B8", fontSize: 11, fontFamily: "JetBrains Mono" };
const TOOL_STYLE = { background: "#0F1424", border: "1px solid rgba(255,255,255,0.1)", borderRadius: 8, color: "#E2E8F0", fontSize: 12 };

const Panel = ({ title, subtitle, children, right, testId }) => (
  <div className="glass rounded-xl p-5" data-testid={testId}>
    <div className="flex items-start justify-between mb-4">
      <div>
        <div className="tech-label">{title}</div>
        {subtitle && <div className="text-sm text-slate-400 mt-1">{subtitle}</div>}
      </div>
      {right}
    </div>
    {children}
  </div>
);

function Cockpit() {
  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 lg:grid-cols-6 gap-3">
        {EXECUTIVE_KPIS.map((k) => (
          <div key={k.label} className="glass rounded-lg p-4" data-testid={`kpi-${k.label.replace(/\s+/g, "-").toLowerCase()}`}>
            <div className="tech-label truncate">{k.label}</div>
            <div className="text-xl font-heading text-white mt-2">{k.value}</div>
            <div className={`text-xs font-mono mt-1 ${k.tone === "positive" ? "text-emerald-300" : k.tone === "warn" ? "text-amber-300" : "text-rose-300"}`}>{k.delta}</div>
          </div>
        ))}
      </div>
      <div className="grid lg:grid-cols-3 gap-4">
        <Panel title="REGIONAL PERFORMANCE · INDEX vs PLAN" testId="chart-region">
          <ResponsiveContainer width="100%" height={220}>
            <BarChart data={REGION_PERFORMANCE}>
              <CartesianGrid stroke="rgba(255,255,255,0.05)" vertical={false} />
              <XAxis dataKey="region" tick={AXIS_STYLE} axisLine={false} tickLine={false} />
              <YAxis tick={AXIS_STYLE} axisLine={false} tickLine={false} domain={[80, 110]} />
              <Tooltip contentStyle={TOOL_STYLE} cursor={{ fill: "rgba(0,229,255,0.05)" }} />
              <Bar dataKey="actual" radius={[4, 4, 0, 0]}>
                {REGION_PERFORMANCE.map((r, i) => (
                  <Cell key={i} fill={r.actual >= 100 ? "#00E5FF" : r.actual >= 96 ? "#0052FF" : "#F97316"} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </Panel>
        <Panel title="CATEGORY PERFORMANCE · SCORE" testId="chart-category">
          <ResponsiveContainer width="100%" height={220}>
            <RadarChart data={CATEGORY_PERFORMANCE}>
              <PolarGrid stroke="rgba(255,255,255,0.1)" />
              <PolarAngleAxis dataKey="category" tick={{ ...AXIS_STYLE, fontSize: 10 }} />
              <Radar dataKey="value" stroke="#00E5FF" fill="#00E5FF" fillOpacity={0.25} />
            </RadarChart>
          </ResponsiveContainer>
        </Panel>
        <Panel title="OPPORTUNITY BY DRIVER · $M" testId="chart-drivers">
          <ResponsiveContainer width="100%" height={220}>
            <BarChart data={OPPORTUNITY_DRIVERS} layout="vertical" margin={{ left: 30 }}>
              <CartesianGrid stroke="rgba(255,255,255,0.05)" horizontal={false} />
              <XAxis type="number" tick={AXIS_STYLE} axisLine={false} tickLine={false} />
              <YAxis dataKey="name" type="category" tick={{ ...AXIS_STYLE, fontSize: 10 }} width={120} axisLine={false} tickLine={false} />
              <Tooltip contentStyle={TOOL_STYLE} />
              <Bar dataKey="value" fill="#0052FF" radius={[0, 4, 4, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </Panel>
      </div>
      <div className="grid lg:grid-cols-3 gap-4">
        <div className="lg:col-span-2">
          <Panel title="NET SALES TREND · $M PER WEEK · PLAN vs ACTUAL" testId="chart-trend">
            <ResponsiveContainer width="100%" height={240}>
              <LineChart data={SALES_TREND}>
                <CartesianGrid stroke="rgba(255,255,255,0.05)" vertical={false} />
                <XAxis dataKey="week" tick={AXIS_STYLE} axisLine={false} tickLine={false} />
                <YAxis tick={AXIS_STYLE} axisLine={false} tickLine={false} />
                <Tooltip contentStyle={TOOL_STYLE} />
                <Line dataKey="plan" stroke="#7C3AED" strokeWidth={2} strokeDasharray="4 4" dot={false} />
                <Line dataKey="actual" stroke="#00E5FF" strokeWidth={2.5} dot={{ r: 3, fill: "#00E5FF" }} />
              </LineChart>
            </ResponsiveContainer>
          </Panel>
        </div>
        <Panel title="TOP ISSUES & RECOMMENDED INTERVENTIONS" testId="panel-interventions">
          <ul className="space-y-3">
            {[
              { i: "PrairieLine fill rate 88%", a: "Escalate replenishment cadence" },
              { i: "47 inactive outlets · Midwest", a: "Deploy field recovery loop" },
              { i: "PR-2203 ROI 0.80", a: "Cancel promo, redirect trade spend" },
              { i: "Beacon cluster ND 42%", a: "Reassign 12 outlets to Dist. A" },
            ].map((x) => (
              <li key={x.i} className="border-l-2 border-cyan-400/40 pl-3">
                <div className="text-sm text-slate-200">{x.i}</div>
                <div className="text-xs text-cyan-300 mt-0.5">→ {x.a}</div>
              </li>
            ))}
          </ul>
        </Panel>
      </div>
    </div>
  );
}

function OpportunityQueue() {
  const [filter, setFilter] = useState("ALL");
  const filtered = filter === "ALL" ? OPPORTUNITIES : OPPORTUNITIES.filter((o) => o.priority === filter);
  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2 flex-wrap">
        <span className="tech-label mr-2">FILTER</span>
        {["ALL", "P0", "P1", "P2"].map((p) => (
          <button key={p} onClick={() => setFilter(p)} className={`px-3 py-1.5 rounded-md text-xs font-mono border ${filter === p ? "bg-cyan-500/15 border-cyan-400/50 text-cyan-200" : "bg-white/[0.02] border-white/10 text-slate-300 hover:border-white/20"}`} data-testid={`queue-filter-${p}`}>
            {p}
          </button>
        ))}
        <span className="ml-auto text-xs text-slate-400 font-mono">{filtered.length} opportunities · ${(filtered.reduce((s, o) => s + o.value, 0) / 1000).toFixed(0)}K potential</span>
      </div>
      <div className="grid lg:grid-cols-2 xl:grid-cols-3 gap-3">
        {filtered.map((o) => (
          <div key={o.id} className="glass rounded-xl p-4 hover:border-cyan-400/30 transition-colors" data-testid={`opp-${o.id}`}>
            <div className="flex items-center justify-between">
              <span className={`text-[10px] font-mono px-2 py-0.5 rounded border ${o.priority === "P0" ? "border-rose-400/50 text-rose-300 bg-rose-500/10" : o.priority === "P1" ? "border-amber-400/50 text-amber-300 bg-amber-500/10" : "border-slate-400/50 text-slate-300 bg-white/5"}`}>{o.priority}</span>
              <span className="text-[10px] font-mono text-slate-500">{o.id}</span>
            </div>
            <div className="mt-2 text-sm text-slate-100 font-medium">{o.outlet}</div>
            <div className="text-xs text-slate-400">{o.geo}</div>
            <div className="mt-3 rounded-md bg-black/30 border border-white/5 p-2.5">
              <div className="tech-label text-[9px]">SIGNAL</div>
              <div className="text-xs text-slate-200">{o.signal}</div>
              <div className="tech-label text-[9px] mt-2">ROOT CAUSE</div>
              <div className="text-xs text-slate-300">{o.root}</div>
            </div>
            <div className="mt-3 grid grid-cols-3 gap-2 text-center">
              <div>
                <div className="tech-label text-[9px]">VALUE</div>
                <div className="text-sm text-cyan-300 font-mono">${(o.value / 1000).toFixed(1)}K</div>
              </div>
              <div>
                <div className="tech-label text-[9px]">CONF.</div>
                <div className="text-sm text-white font-mono">{(o.confidence * 100).toFixed(0)}%</div>
              </div>
              <div>
                <div className="tech-label text-[9px]">DUE</div>
                <div className="text-sm text-white font-mono">{o.due}</div>
              </div>
            </div>
            <div className="mt-3 text-xs text-slate-200">→ {o.action}</div>
            <div className="mt-3 flex items-center justify-between">
              <span className="text-[10px] text-slate-400 font-mono">Owner: {o.owner} · Evidence: {o.evidence}</span>
              <button onClick={() => toast.success(`Action created for ${o.id}`, { description: o.action })} className="px-3 py-1.5 rounded-md text-xs btn-primary" data-testid={`opp-${o.id}-create`}>Create action</button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function OutletIntel() {
  const [selected, setSelected] = useState(null);
  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-3">
        {[
          { l: "Active outlets", v: "38,412" }, { l: "Inactive outlets", v: "2,061" }, { l: "Lost outlets", v: "487" }, { l: "Assortment gaps", v: "6,240" }, { l: "Visits overdue", v: "1,152" },
        ].map((k) => (
          <div key={k.l} className="glass rounded-lg p-4">
            <div className="tech-label">{k.l}</div>
            <div className="text-xl font-heading text-white mt-2">{k.v}</div>
          </div>
        ))}
      </div>
      <div className="glass rounded-xl overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead className="bg-white/5">
              <tr className="text-left text-slate-400">
                {["Outlet", "City", "Status", "Last Billed", "Freq Δ", "Potential", "Gap SKUs", "Visit", ""].map((h) => (
                  <th key={h} className="px-4 py-3 font-mono uppercase tracking-wider text-[10px]">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {OUTLETS.map((o) => (
                <tr key={o.id} className="border-t border-white/5 hover:bg-white/[0.03] cursor-pointer" onClick={() => setSelected(o)} data-testid={`outlet-${o.id}`}>
                  <td className="px-4 py-3"><div className="text-slate-100">{o.name}</div><div className="text-[10px] text-slate-500 font-mono">{o.id}</div></td>
                  <td className="px-4 py-3 text-slate-300">{o.city}</td>
                  <td className="px-4 py-3"><span className={`text-[10px] font-mono px-2 py-0.5 rounded ${o.status === "Inactive" || o.status === "At Risk" ? "bg-rose-500/10 text-rose-300" : o.status === "Declining" ? "bg-amber-500/10 text-amber-300" : "bg-emerald-500/10 text-emerald-300"}`}>{o.status}</span></td>
                  <td className="px-4 py-3 text-slate-300 font-mono">{o.lastBilled}</td>
                  <td className="px-4 py-3 text-rose-300 font-mono">{o.freq}</td>
                  <td className="px-4 py-3 text-cyan-300 font-mono">${(o.potential / 1000).toFixed(1)}K</td>
                  <td className="px-4 py-3 text-slate-300">{o.gap}</td>
                  <td className="px-4 py-3"><span className={`text-[10px] font-mono ${o.visit === "Overdue" ? "text-rose-300" : o.visit === "Due" ? "text-amber-300" : "text-emerald-300"}`}>{o.visit}</span></td>
                  <td className="px-4 py-3 text-cyan-400"><ArrowRight size={14} /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
      <Dialog open={!!selected} onOpenChange={() => setSelected(null)}>
        <DialogContent className="bg-[#0F1424] border-white/10 text-slate-100 sm:max-w-[640px]">
          {selected && (
            <>
              <DialogHeader>
                <div className="tech-label">OUTLET · {selected.id}</div>
                <DialogTitle className="font-heading tracking-tight">{selected.name}</DialogTitle>
              </DialogHeader>
              <div className="grid grid-cols-2 gap-3">
                {[
                  { l: "City", v: selected.city }, { l: "Status", v: selected.status }, { l: "Last billed", v: selected.lastBilled }, { l: "Freq change", v: selected.freq }, { l: "Revenue potential", v: `$${(selected.potential / 1000).toFixed(1)}K` }, { l: "Assortment gap", v: `${selected.gap} SKUs` }, { l: "Visit status", v: selected.visit }, { l: "Recommended", v: "Field visit + kit deploy" },
                ].map((x) => (
                  <div key={x.l} className="rounded-md bg-white/[0.03] border border-white/5 p-3">
                    <div className="tech-label">{x.l}</div>
                    <div className="text-sm text-white mt-1">{x.v}</div>
                  </div>
                ))}
              </div>
              <button onClick={() => { toast.success(`Recovery action created for ${selected.name}`); setSelected(null); }} className="mt-4 w-full px-4 py-3 rounded-md text-sm btn-primary" data-testid="outlet-drawer-action">Create recovery action</button>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function DistributorHealth() {
  return (
    <div className="space-y-3">
      <DistributorNetworkMap onSelect={(d) => toast(`${d.name}`, { description: `${d.region} · Score ${d.score} · Credit ${d.credit}` })} />
      {DISTRIBUTORS.map((d) => (
        <div key={d.id} className="glass rounded-xl p-5" data-testid={`dist-${d.id}`}>
          <div className="grid lg:grid-cols-12 gap-4 items-center">
            <div className="lg:col-span-3">
              <div className="text-[10px] font-mono text-slate-500">{d.id} · {d.region}</div>
              <div className="font-heading text-white mt-0.5">{d.name}</div>
              <div className="mt-2 flex items-center gap-2">
                <div className="w-16 h-16 rounded-lg border border-white/10 flex items-center justify-center relative overflow-hidden">
                  <svg viewBox="0 0 36 36" className="w-full h-full">
                    <circle cx="18" cy="18" r="15" fill="none" stroke="rgba(255,255,255,0.08)" strokeWidth="3" />
                    <circle cx="18" cy="18" r="15" fill="none" stroke={d.score >= 75 ? "#22C55E" : d.score >= 65 ? "#00E5FF" : "#F97316"} strokeWidth="3" strokeDasharray={`${d.score} 100`} strokeLinecap="round" transform="rotate(-90 18 18)" />
                  </svg>
                  <span className="absolute text-sm font-mono text-white">{d.score}</span>
                </div>
                <div>
                  <div className="tech-label">HEALTH</div>
                  <div className="text-xs text-slate-300">Credit: <span className={`font-mono ${d.credit === "Red" ? "text-rose-300" : d.credit === "Amber" ? "text-amber-300" : "text-emerald-300"}`}>{d.credit}</span></div>
                </div>
              </div>
            </div>
            <div className="lg:col-span-6 grid grid-cols-3 sm:grid-cols-5 gap-3">
              {[
                { l: "Sales Δ", v: `${d.sales}%`, t: d.sales < 0 ? "rose" : "emerald" },
                { l: "Stock cover", v: `${d.cover}d`, t: d.cover < 8 ? "rose" : "cyan" },
                { l: "Ageing", v: `${d.ageing}d`, t: d.ageing > 20 ? "amber" : "cyan" },
                { l: "Returns", v: `${d.returns}%`, t: d.returns > 4 ? "rose" : "cyan" },
                { l: "Fill rate", v: `${d.fill}%`, t: d.fill < 90 ? "rose" : "emerald" },
              ].map((k) => (
                <div key={k.l} className="rounded-md bg-white/[0.02] border border-white/5 p-2.5">
                  <div className="tech-label text-[9px]">{k.l}</div>
                  <div className={`text-sm font-mono mt-1 ${k.t === "rose" ? "text-rose-300" : k.t === "amber" ? "text-amber-300" : k.t === "emerald" ? "text-emerald-300" : "text-cyan-300"}`}>{k.v}</div>
                </div>
              ))}
            </div>
            <div className="lg:col-span-3">
              <div className="tech-label">RISK DRIVERS</div>
              <div className="flex flex-wrap gap-1.5 mt-2">
                {d.drivers.map((x) => <span key={x} className="text-[10px] font-mono px-2 py-0.5 rounded bg-white/5 border border-white/10 text-slate-300">{x}</span>)}
              </div>
              <button onClick={() => toast.success(`Intervention plan drafted for ${d.name}`)} className="mt-3 text-xs text-cyan-300 hover:text-cyan-200" data-testid={`dist-${d.id}-plan`}>Recommend intervention →</button>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}

function PromoIntel() {
  const RecColor = { Continue: "text-emerald-300 bg-emerald-500/10 border-emerald-400/30", Redesign: "text-amber-300 bg-amber-500/10 border-amber-400/30", Cancel: "text-rose-300 bg-rose-500/10 border-rose-400/30" };
  return (
    <div className="glass rounded-xl overflow-hidden">
      <div className="overflow-x-auto">
        <table className="w-full text-xs">
          <thead className="bg-white/5">
            <tr className="text-left text-slate-400">
              {["Promotion", "Trade Spend", "Incremental", "ROI", "Uplift", "Cannib.", "Exec", "Base", "Promo Vol", "Recommendation"].map((h) => <th key={h} className="px-4 py-3 font-mono uppercase tracking-wider text-[10px]">{h}</th>)}
            </tr>
          </thead>
          <tbody>
            {PROMOTIONS.map((p) => (
              <tr key={p.id} className="border-t border-white/5 hover:bg-white/[0.03]" data-testid={`promo-${p.id}`}>
                <td className="px-4 py-3"><div className="text-slate-100">{p.name}</div><div className="text-[10px] text-slate-500 font-mono">{p.id}</div></td>
                <td className="px-4 py-3 font-mono text-slate-200">${(p.trade / 1000).toFixed(0)}K</td>
                <td className="px-4 py-3 font-mono text-cyan-300">${(p.incr / 1000).toFixed(0)}K</td>
                <td className="px-4 py-3 font-mono text-white">{p.roi}x</td>
                <td className="px-4 py-3 font-mono text-emerald-300">{p.uplift}</td>
                <td className="px-4 py-3 font-mono text-rose-300">{p.cannib}</td>
                <td className="px-4 py-3 font-mono">{p.exec}%</td>
                <td className="px-4 py-3 font-mono text-slate-300">{p.base}</td>
                <td className="px-4 py-3 font-mono text-slate-100">{p.promo}</td>
                <td className="px-4 py-3"><span className={`text-[10px] font-mono px-2 py-0.5 rounded border ${RecColor[p.recommendation]}`}>{p.recommendation.toUpperCase()}</span></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function ScenarioSimulator() {
  const [outlets, setOutlets] = useState(1200);
  const [conversion, setConversion] = useState(35);
  const [avgValue, setAvgValue] = useState(180);
  const [uplift, setUplift] = useState(12);
  const [cost, setCost] = useState(80);
  const [probability, setProbability] = useState(70);
  const [cannib, setCannib] = useState(5);

  const calc = useMemo(() => {
    const gross = outlets * (conversion / 100) * avgValue * (1 + uplift / 100);
    const cannibLoss = gross * (cannib / 100);
    const net = gross - cannibLoss - outlets * cost * (conversion / 100);
    const expected = net * (probability / 100);
    return {
      conservative: Math.round(expected * 0.75),
      expected: Math.round(expected),
      upside: Math.round(expected * 1.35),
      roi: net === 0 ? 0 : (net / Math.max(1, outlets * cost * (conversion / 100))).toFixed(2),
      payback: net > 0 ? Math.max(1, Math.round((outlets * cost * (conversion / 100)) / (net / 12))) : "—",
    };
  }, [outlets, conversion, avgValue, uplift, cost, probability, cannib]);

  const Ctrl = ({ label, value, unit, onChange, min, max, step = 1, testId }) => (
    <div>
      <div className="flex items-center justify-between mb-2">
        <span className="tech-label">{label}</span>
        <span className="font-mono text-sm text-cyan-300">{value}{unit}</span>
      </div>
      <Slider value={[value]} min={min} max={max} step={step} onValueChange={(v) => onChange(v[0])} data-testid={testId} />
    </div>
  );

  return (
    <div className="grid lg:grid-cols-5 gap-4">
      <div className="lg:col-span-3 glass rounded-xl p-6 space-y-5">
        <div className="tech-label">SCENARIO INPUTS</div>
        <Ctrl label="Outlets targeted" value={outlets} unit="" onChange={setOutlets} min={100} max={5000} step={50} testId="sim-outlets" />
        <Ctrl label="Expected conversion" value={conversion} unit="%" onChange={setConversion} min={5} max={80} testId="sim-conversion" />
        <Ctrl label="Avg outlet value" value={avgValue} unit="$" onChange={setAvgValue} min={50} max={800} step={10} testId="sim-value" />
        <Ctrl label="Promotion uplift" value={uplift} unit="%" onChange={setUplift} min={0} max={40} testId="sim-uplift" />
        <Ctrl label="Cost of intervention" value={cost} unit="$" onChange={setCost} min={10} max={300} testId="sim-cost" />
        <Ctrl label="Probability of success" value={probability} unit="%" onChange={setProbability} min={10} max={95} testId="sim-prob" />
        <Ctrl label="Cannibalization" value={cannib} unit="%" onChange={setCannib} min={0} max={25} testId="sim-cannib" />
      </div>
      <div className="lg:col-span-2 space-y-3">
        {[
          { l: "Conservative", v: calc.conservative, t: "text-slate-100" },
          { l: "Expected", v: calc.expected, t: "text-cyan-300" },
          { l: "Upside", v: calc.upside, t: "text-emerald-300" },
        ].map((r) => (
          <div key={r.l} className="glass rounded-xl p-5" data-testid={`sim-${r.l.toLowerCase()}`}>
            <div className="tech-label">{r.l.toUpperCase()} OUTCOME</div>
            <div className={`text-3xl font-heading mt-2 ${r.t}`}>${r.v.toLocaleString()}</div>
          </div>
        ))}
        <div className="grid grid-cols-2 gap-3">
          <div className="glass rounded-xl p-4">
            <div className="tech-label">ROI</div>
            <div className="text-xl font-heading text-white mt-1 font-mono">{calc.roi}x</div>
          </div>
          <div className="glass rounded-xl p-4">
            <div className="tech-label">PAYBACK</div>
            <div className="text-xl font-heading text-white mt-1 font-mono">{calc.payback} mo</div>
          </div>
        </div>
      </div>
    </div>
  );
}

function AICopilot() {
  const [messages, setMessages] = useState([{ role: "system", text: "Ask about performance, distributors, promotions, outlets or weekly changes. Live Claude Sonnet 5 · grounded on NorthStar Foods context." }]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [sessionId] = useState(() => `xy-${Math.random().toString(36).slice(2, 10)}`);

  const API = process.env.REACT_APP_BACKEND_URL + "/api";

  const send = async (q) => {
    const query = (q || input).trim();
    if (!query || loading) return;
    setMessages((m) => [...m, { role: "user", text: query }]);
    setInput("");
    setLoading(true);
    try {
      const res = await fetch(`${API}/copilot/ask`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ question: query, product: "commercial", session_id: sessionId }),
      });
      if (!res.ok) throw new Error(`API ${res.status}`);
      const data = await res.json();
      setMessages((m) => [...m, { role: "assistant", ...data }]);
    } catch (err) {
      // Graceful fallback to pre-scripted answers
      const found = COPILOT_QA.find((x) => x.q.toLowerCase() === query.toLowerCase()) || COPILOT_QA.find((x) => query.toLowerCase().split(" ").some((w) => x.q.toLowerCase().includes(w) && w.length > 4));
      const answer = found || { root: "Unable to reach the live copilot right now. Try one of the suggested queries below.", metrics: [], actions: [], impact: "-", confidence: 0, sources: [], refreshed: new Date().toISOString().slice(0, 16) };
      setMessages((m) => [...m, { role: "assistant", ...answer }]);
    } finally {
      setLoading(false);
    }
  };
  return (
    <div className="grid lg:grid-cols-4 gap-4">
      <div className="lg:col-span-3 glass rounded-xl p-5 min-h-[520px] flex flex-col">
        <div className="tech-label flex items-center gap-2">
          <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 pulse-ring inline-block" />
          XYLOCTRA COPILOT · CLAUDE SONNET 5 · GROUNDED · AUDITED
        </div>
        <div className="mt-4 flex-1 space-y-4 overflow-y-auto max-h-[520px]">
          {messages.map((m, i) => (
            <div key={i}>
              {m.role === "system" && <div className="text-xs text-slate-500 italic">{m.text}</div>}
              {m.role === "user" && <div className="text-sm text-slate-200 bg-white/5 rounded-md p-3 border border-white/5">{m.text}</div>}
              {m.role === "assistant" && (
                <div className="rounded-md border border-cyan-400/20 bg-cyan-500/5 p-4" data-testid={`copilot-answer-${i}`}>
                  <div className="flex items-center gap-2 mb-2"><Sparkles size={14} className="text-cyan-300" /><span className="tech-label">RESPONSE</span><span className="ml-auto text-[10px] text-slate-500 font-mono">Conf. {(m.confidence * 100 || 0).toFixed(0)}% · {m.refreshed}</span></div>
                  <div className="text-sm text-slate-100 leading-relaxed">{m.root}</div>
                  {m.metrics?.length > 0 && (
                    <div className="mt-3 grid grid-cols-2 sm:grid-cols-4 gap-2">
                      {m.metrics.map((k) => <div key={k.k} className="bg-black/30 border border-white/5 rounded p-2"><div className="tech-label text-[9px]">{k.k}</div><div className="text-sm text-white mt-0.5 font-mono">{k.v}</div></div>)}
                    </div>
                  )}
                  {m.actions?.length > 0 && (
                    <div className="mt-3">
                      <div className="tech-label">RECOMMENDED ACTIONS</div>
                      <ul className="mt-1 text-sm text-slate-200 space-y-1">{m.actions.map((a) => <li key={a}>→ {a}</li>)}</ul>
                    </div>
                  )}
                  {m.impact !== "-" && <div className="mt-3 text-sm text-cyan-300">Estimated impact: <span className="font-mono">{m.impact}</span></div>}
                  {m.sources?.length > 0 && <div className="mt-3 flex flex-wrap gap-1.5">{m.sources.map((s) => <span key={s} className="text-[10px] font-mono px-2 py-0.5 rounded bg-white/5 border border-white/10 text-slate-400">{s}</span>)}</div>}
                </div>
              )}
            </div>
          ))}
        </div>
        <div className="mt-4 flex gap-2">
          <input value={input} onChange={(e) => setInput(e.target.value)} onKeyDown={(e) => e.key === "Enter" && send()} placeholder={loading ? "Thinking…" : "Ask about performance, distributors, promotions..."} disabled={loading} className="flex-1 rounded-md bg-black/40 border border-white/10 px-3 py-2 text-sm text-slate-100 focus:outline-none focus:border-cyan-400/40 disabled:opacity-60" data-testid="copilot-input" />
          <button onClick={() => send()} disabled={loading} className="px-4 py-2 rounded-md text-sm btn-primary inline-flex items-center gap-2 disabled:opacity-60" data-testid="copilot-send"><Send size={14} /> {loading ? "…" : "Ask"}</button>
        </div>
      </div>
      <div className="glass rounded-xl p-5">
        <div className="tech-label mb-3">SUGGESTED QUERIES</div>
        <div className="space-y-2">
          {COPILOT_QA.map((q, i) => (
            <button key={q.q} onClick={() => send(q.q)} className="w-full text-left text-sm text-slate-300 hover:text-cyan-200 border border-white/5 hover:border-cyan-400/30 rounded-md p-3 transition-colors" data-testid={`copilot-suggest-${i}`}>{q.q}</button>
          ))}
        </div>
      </div>
    </div>
  );
}

function ActionTracker() {
  const stats = useMemo(() => {
    const open = ACTIONS.filter((a) => a.status !== "Completed").length;
    const completed = ACTIONS.filter((a) => a.status === "Completed").length;
    const overdue = ACTIONS.filter((a) => a.status === "Overdue").length;
    const acceptance = Math.round((ACTIONS.filter((a) => a.accepted).length / ACTIONS.length) * 100);
    const completion = Math.round((completed / ACTIONS.length) * 100);
    const realized = ACTIONS.reduce((s, a) => s + a.realized, 0);
    return { open, completed, overdue, acceptance, completion, realized };
  }, []);
  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 lg:grid-cols-6 gap-3">
        {[
          { l: "Open", v: stats.open }, { l: "Completed", v: stats.completed }, { l: "Overdue", v: stats.overdue }, { l: "Acceptance", v: `${stats.acceptance}%` }, { l: "Completion", v: `${stats.completion}%` }, { l: "Realized value", v: `$${(stats.realized / 1000).toFixed(1)}K` },
        ].map((k) => (
          <div key={k.l} className="glass rounded-lg p-4"><div className="tech-label">{k.l}</div><div className="text-xl font-heading text-white mt-2">{k.v}</div></div>
        ))}
      </div>
      <div className="glass rounded-xl overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead className="bg-white/5"><tr className="text-left text-slate-400">{["Recommendation", "Owner", "Priority", "Due", "Status", "Accepted", "Expected", "Realized", "Closure"].map((h) => <th key={h} className="px-4 py-3 font-mono uppercase tracking-wider text-[10px]">{h}</th>)}</tr></thead>
            <tbody>
              {ACTIONS.map((a) => (
                <tr key={a.id} className="border-t border-white/5 hover:bg-white/[0.03]" data-testid={`action-${a.id}`}>
                  <td className="px-4 py-3"><div className="text-slate-100">{a.rec}</div><div className="text-[10px] text-slate-500 font-mono">{a.id}</div></td>
                  <td className="px-4 py-3 text-slate-300">{a.owner}</td>
                  <td className="px-4 py-3"><span className={`text-[10px] font-mono ${a.pri === "P0" ? "text-rose-300" : a.pri === "P1" ? "text-amber-300" : "text-slate-300"}`}>{a.pri}</span></td>
                  <td className="px-4 py-3 font-mono text-slate-300">{a.due}</td>
                  <td className="px-4 py-3"><span className={`text-[10px] font-mono px-2 py-0.5 rounded ${a.status === "Completed" ? "bg-emerald-500/10 text-emerald-300" : a.status === "Overdue" ? "bg-rose-500/10 text-rose-300" : a.status === "Approval" ? "bg-violet-500/10 text-violet-300" : "bg-cyan-500/10 text-cyan-300"}`}>{a.status}</span></td>
                  <td className="px-4 py-3">{a.accepted ? "✓" : "—"}</td>
                  <td className="px-4 py-3 font-mono text-slate-200">${(a.expected / 1000).toFixed(1)}K</td>
                  <td className="px-4 py-3 font-mono text-cyan-300">${(a.realized / 1000).toFixed(1)}K</td>
                  <td className="px-4 py-3 font-mono text-slate-400">{a.closure}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

function DataLineage() {
  const stages = [
    { k: "Source", d: "ERP.Sales · DMS.Inventory · SFA.Visits · POS", meta: "12 systems · 87 tables" },
    { k: "Raw", d: "Landed in S3 raw zone", meta: "42 GB / day" },
    { k: "Validation", d: "24 quality rules · dedup · schema drift", meta: "99.4% pass rate" },
    { k: "Curated", d: "Star schema · governed masters", meta: "Model v4.2" },
    { k: "KPI", d: "Net Sales · Fill Rate · ND · Cover", meta: "48 KPIs" },
    { k: "Signal", d: "Inactive · Assortment gap · Stock cover", meta: "11 detectors" },
    { k: "Opportunity", d: "Scored · rationale · confidence", meta: "1,840 open" },
    { k: "Recommendation", d: "Next-best-action + owner", meta: "Approved" },
    { k: "Action", d: "Workflow · SLA · approvals", meta: "Live" },
    { k: "Impact", d: "Realized $ · reconciled", meta: "$1.1M QTD" },
  ];
  return (
    <div className="space-y-3">
      <div className="glass rounded-xl p-6">
        <div className="tech-label mb-4">SOURCE → RAW → VALIDATION → CURATED → KPI → SIGNAL → OPPORTUNITY → RECOMMENDATION → ACTION → REALIZED IMPACT</div>
        <div className="grid md:grid-cols-2 lg:grid-cols-5 gap-3">
          {stages.map((s, i) => (
            <div key={s.k} className="rounded-lg border border-white/10 bg-white/[0.02] p-3 relative">
              <div className="flex items-center justify-between">
                <span className="tech-label">{`0${i + 1}`.slice(-2)}</span>
                <span className="text-cyan-400"><ArrowRight size={12} /></span>
              </div>
              <div className="font-heading text-white mt-1">{s.k}</div>
              <div className="text-xs text-slate-400 mt-1">{s.d}</div>
              <div className="text-[10px] font-mono text-cyan-300 mt-2">{s.meta}</div>
            </div>
          ))}
        </div>
      </div>
      <div className="glass rounded-xl p-5">
        <div className="tech-label mb-3">INSPECTION · OPPORTUNITY OPP-4421</div>
        <div className="grid md:grid-cols-3 gap-3">
          {[
            { l: "Source file", v: "erp_sales_2026w06.parquet" }, { l: "Refresh time", v: "2026-02-10 06:14 UTC" }, { l: "Validation checks", v: "24 / 24 passed" }, { l: "Calculation version", v: "kpi.v4.2.1" }, { l: "Model version", v: "signal.opportunity.v3.6" }, { l: "Confidence", v: "0.86" }, { l: "User decision", v: "Accepted by R. Alvarez" }, { l: "Outcome", v: "Closed · $38.5K realized" },
          ].map((x) => (
            <div key={x.l} className="rounded-md bg-black/30 border border-white/5 p-3"><div className="tech-label">{x.l}</div><div className="text-sm text-white mt-1 font-mono">{x.v}</div></div>
          ))}
        </div>
      </div>
    </div>
  );
}

const MODULES = { cockpit: Cockpit, queue: OpportunityQueue, outlets: OutletIntel, distributors: DistributorHealth, promos: PromoIntel, scenario: ScenarioSimulator, copilot: AICopilot, actions: ActionTracker, lineage: DataLineage };

export default function DemoCommercial() {
  const [active, setActive] = useState("cockpit");
  const Comp = MODULES[active];
  const meta = MENU.find((m) => m.key === active);
  return (
    <div className="min-h-screen text-slate-100">
      <Navbar />
      <div className="pt-16 flex min-h-screen">
        <aside className="hidden lg:flex flex-col w-64 border-r border-white/5 bg-[#0B1021]/80 backdrop-blur-xl fixed left-0 top-16 bottom-0" data-testid="demo-sidebar">
          <div className="p-5 border-b border-white/5">
            <div className="tech-label">PRODUCT</div>
            <div className="font-heading text-white mt-1 tracking-tight">Commercial Excellence</div>
            <div className="tech-label mt-2 text-[9px] text-slate-500">TENANT · NORTHSTAR-FOODS</div>
          </div>
          <nav className="p-3 flex-1 space-y-1">
            {MENU.map((m) => (
              <button key={m.key} onClick={() => setActive(m.key)} className={`w-full flex items-center gap-2.5 px-3 py-2 rounded-md text-sm text-left transition-colors ${active === m.key ? "bg-cyan-500/10 border border-cyan-400/30 text-cyan-100" : "text-slate-300 hover:bg-white/[0.03] border border-transparent"}`} data-testid={`sidebar-${m.key}`}>
                <m.icon size={15} />
                {m.label}
              </button>
            ))}
          </nav>
          <div className="p-4 text-[10px] font-mono text-slate-500 border-t border-white/5">
            SYNC · ACTIVE<br />ENV · PROD · CA-CENTRAL-1<br />BUILD v4.2.1
          </div>
        </aside>
        <main className="lg:ml-64 flex-1 p-6 lg:p-8">
          <div className="mb-6 flex flex-wrap items-center gap-3">
            <meta.icon size={18} className="text-cyan-300" />
            <h1 className="font-heading text-2xl text-white tracking-tight">{meta.label}</h1>
            <span className="ml-auto text-[10px] font-mono text-slate-500 border border-white/10 rounded px-2 py-1">{SYNTHETIC_DISCLAIMER}</span>
          </div>
          <div className="lg:hidden flex overflow-x-auto gap-2 pb-3 mb-4">
            {MENU.map((m) => (
              <button key={m.key} onClick={() => setActive(m.key)} className={`flex-shrink-0 px-3 py-1.5 rounded-md text-xs border ${active === m.key ? "bg-cyan-500/10 border-cyan-400/30 text-cyan-200" : "border-white/10 text-slate-300"}`}>{m.label}</button>
            ))}
          </div>
          <Comp />
        </main>
      </div>
    </div>
  );
}
